import { Box, Typography, useTheme } from '@mui/material';
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { tokens } from '../../global/theme/tokens.js';
import moment from 'moment';
import { ExoCalDay } from './cal/ExoCalDay.tsx';
import { useTranslation } from 'react-i18next';
import { ExoCalCol } from './cal/ExoCalCol.tsx';
import Feedback from '../special/Feedback.jsx';
import { updateOrCreate } from '../special/updateOrCreate.js';
import { ExoCalPopper } from './cal/ExoCalPopper.jsx';
import Cookies from 'universal-cookie';
import ExoCalWeekView from './ExoCalWeekView.jsx';
import { generateDaysWithEvents } from './cal/generateDaysWithEvents.js';
import useMediaQuery from '@mui/material/useMediaQuery';
import { BlockedUserTimes } from './cal/BlockedUserTimes.jsx';

const ExoCal = forwardRef(
  (
    {
      events,
      calendars,
      onDateChange,
      startDate,
      onEventsChange,
      noBlur,
      noBackground,
      selectedPeriod,
      openBlockedTimes,
      blockedUserTimesData,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

    const { t } = useTranslation();

    const [days, setDays] = useState();
    const [editMode, setEditMode] = useState(false);

    const cookies = new Cookies();
    const userId = cookies.get('userId');

    const [useSidebar, setUseSidebar] = useState(false);

    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    useEffect(() => {
      setDays(
        generateDaysWithEvents(
          startDate,
          selectedPeriod,
          events,
          calendars,
          userId
        )
      );
    }, [startDate, events, calendars, selectedPeriod]);

    // feedback
    const [alertState, setAlertState] = useState({
      alertOpen: false,
      alertType: 'info',
      alertText: 'test',
    });
    function handleFeedback(text, type) {
      setAlertState({ alertOpen: true, alertText: text, alertType: type });
    }
    const columns = [
      t('Monday'),
      t('Tuesday'),
      t('Wednesday'),
      t('Thursday'),
      t('Friday'),
      t('Saturday'),
      t('Sunday'),
    ];

    // Drag & Drop States
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);
    const [selectedRange, setSelectedRange] = useState(new Set());
    const [popperOpen, setPopperOpen] = useState(false);
    const [selectionData, setSelectionData] = useState(null);

    useImperativeHandle(ref, () => ({ setPopperOpen }));

    // Handle Mouse Down (Start Selection)
    function handleMouseDown(date) {
      if (popperOpen) return; // Prevent setting popperOpen if already open

      setDragStart(date);
      setDragEnd(date);
    }

    // Handle Mouse Move (Update Selection)
    function handleMouseMove(date) {
      if (!dragStart || dragStart?.isSame(date, 'day')) return;

      const range = new Set();

      if (!dragStart.isSame(dragEnd, 'day') && selectedRange.size === 0)
        range.add(dragStart.format('YYYY-MM-DD'));

      setDragEnd(date);

      const start = moment.min(dragStart, date);
      const end = moment.max(dragStart, date);

      let current = start.clone();
      while (current.isSameOrBefore(end, 'day')) {
        range.add(current.format('YYYY-MM-DD'));
        current.add(1, 'day');
      }
      setSelectedRange(range);
    }

    // Handle Mouse Up (Finish Selection)
    function handleMouseUp() {
      if (!dragStart || dragStart?.isSame(dragEnd, 'day')) {
        setDragStart(null);
        setDragEnd(null);
        return;
      }
      setPopperOpen(true); // Ensure Popper stays open after selection
      if (dragStart && dragEnd) {
        if (dragStart > dragEnd) {
          setSelectionData({
            startDateTime: dragEnd.format(),
            endDateTime: dragStart.format(),
          });
        } else {
          setSelectionData({
            startDateTime: dragStart.format(),
            endDateTime: dragEnd.format(),
          });
        }
      }
      setDragStart(null);
      setDragEnd(null);
    }

    // Handle Double-Click for Single-Day Event
    function handleDoubleClick(date) {
      if (popperOpen) return;

      // Current time rounded to the next 30-minute mark
      const now = moment();
      const roundedStart = now
        .clone()
        .add(30 - (now.minute() % 30), 'minutes')
        .startOf('minute');

      const roundedEnd = roundedStart.clone().add(30, 'minutes');

      // Merge clicked date + rounded times
      const startDate = date
        .clone()
        .hour(roundedStart.hour())
        .minute(roundedStart.minute())
        .second(0);

      const endDate = date
        .clone()
        .hour(roundedEnd.hour())
        .minute(roundedEnd.minute())
        .second(0);

      setSelectionData({
        startDateTime: startDate.format(),
        endDateTime: endDate.format(),
        wholeDay: false,
      });

      setSelectedRange(new Set([date.format('YYYY-MM-DD')]));
      setPopperOpen(true);
    }

    // popper
    const [popperAnchor, setPopperAnchor] = useState(null);

    function handleUpdatePopper(event, day) {
      setPopperAnchor(event.currentTarget);
      setPopperOpen(false);
    }

    function handleClosePopper() {
      setPopperOpen(false); // close popper for single-day event
      setSelectedRange(new Set());
      setDragEnd(null);
      setDragStart(null);
      setSelectionData({ ...selectionData, id: null });
    }
    function handleCloseSidebar() {
      setPopperOpen(false);
      setUseSidebar(false); // close sidebar for single-day event
      setSelectedRange(new Set());
      setSelectionData(null);
    }

    function handleUpdate(res) {
      const newEvents = updateOrCreate(events, res);
      onEventsChange(newEvents);
    }

    // Handle Create Event in Day View
    function handleCreateEventInDayView(event) {
      setSelectionData(event);
      setUseSidebar(true);
    }

    // event interaction
    function handleEventSelect(e, event) {
      if (editMode) return;

      if (selectedPeriod === 'day' && isDesktop) {
        setUseSidebar(true);
      } else {
        setPopperAnchor(e.currentTarget);
        setPopperOpen(true);
      }

      setSelectionData(event);
    }

    function handleEventEdit(e, event) {
      if (!event.writePermissions || editMode) return;
      setPopperAnchor(e.currentTarget);

      if (selectedPeriod !== 'day') {
        setPopperOpen(true);
      }

      setSelectionData(event);
      setEditMode(true);
    }

    function handleDelete(eventId, seriesMasterId) {
      const newEvents = events.filter((obj) =>
        seriesMasterId
          ? obj.seriesMasterId !== seriesMasterId && obj.id !== seriesMasterId
          : obj.id !== eventId,
      );
      onEventsChange(newEvents);
    }

    return (
      <Box className="flex flex-col-reverse md:flex-row h-full w-full gap-2 overflow-hidden">
        <Box
          className="w-full h-full flex flex-col gap-1 overflow-y-auto rounded-lg"
          onMouseUp={handleMouseUp}
          sx={{
            bgcolor: noBackground ? 'transparent' : colors.glass,
            backdropFilter: noBlur ? 'unset' : 'blur(10px)',
          }}
        >
          {selectedPeriod === 'month' ? (
            <>
              <Box className="w-full grid [grid-template-columns:25px_repeat(7,minmax(0,1fr))] gap-1 sticky top-0 z-20">
                <Box className="flex items-center pl-1 pt-1 opacity-50">
                  <Typography fontSize={10}>{t('CW')}</Typography>
                </Box>
                {columns.map((col) => (
                  <ExoCalCol key={col} label={col} noBlur={noBlur} />
                ))}
              </Box>
              <Box
                className="w-full h-full grid [grid-template-columns:25px_repeat(7,minmax(0,1fr))] rounded-md"
                sx={{
                  gridAutoRows: '1fr',
                }}
              >
                {days &&
                  days.map((day, index) => {
                    const dayString = day.date.format('YYYY-MM-DD');
                    const isSelected = selectedRange.has(dayString);

                    // Check if day is the first or last in the range
                    const isFirst = day.date.isSame(
                      dragStart || selectionData?.start,
                      'day'
                    );
                    const isLast = day.date.isSame(
                      dragEnd || selectionData?.end,
                      'day'
                    );

                    const dragIsInverted =
                      dragStart &&
                      dragEnd &&
                      moment(dragStart).isAfter(dragEnd);

                    const colorVals = colors.card
                      .split('(')
                      .pop()
                      .slice(0, -1)
                      .split(',');
                    const newOp = Number(colorVals.pop()) * 0.3;
                    const newColCard = `rgba(${colorVals[0]}, ${colorVals[1]}, ${colorVals[2]}, ${newOp})`;

                    return (
                      <Fragment key={day.id}>
                        {index % 7 == 0 && (
                          <Box
                            className="pt-1 flex justify-center"
                            sx={{
                              borderWidth: '1px 0px 0px 0px',
                              borderColor:
                                index == 0 && moment(day.date).date() != 1
                                  ? newColCard
                                  : colors.card,
                            }}
                          >
                            <Typography className="opacity-50" fontSize={10}>
                              {moment(day.date).isoWeek()}
                            </Typography>
                          </Box>
                        )}
                        <ExoCalDay
                          key={day.date.format('DD-MM-YYYY')}
                          day={day}
                          noBlur={noBlur}
                          selectedEvent={selectionData}
                          isSelected={isSelected}
                          isFirst={dragIsInverted ? isLast : isFirst} // Pass first entry flag
                          isLast={dragIsInverted ? isFirst : isLast} // Pass last entry flag
                          selectionActive={selectedRange.size > 0}
                          onMouseDown={(e) => {
                            if (popperOpen || e.button !== 0) return;
                            e.preventDefault();
                            handleMouseDown(day.date);
                            handleUpdatePopper(e, day.date);
                          }}
                          onMouseEnter={(e) => {
                            if (dragStart) {
                              if (day.date.isBefore(dragStart))
                                handleUpdatePopper(e, day.date);
                              handleMouseMove(day.date);
                            }
                          }}
                          onDoubleClick={() => {
                            handleDoubleClick(day.date); // Handle double-click for single day event
                          }}
                          onEventSelect={handleEventSelect}
                          onEventEdit={handleEventEdit}
                        />
                      </Fragment>
                    );
                  })}
              </Box>
            </>
          ) : (
            <ExoCalWeekView
              labelFormat="dddd"
              onCancel={() => {}}
              format="DD.MM.YYYY"
              onSubmit={() => {}}
              userId={userId}
              dateStart={startDate}
              edit
              hourStart={6}
              hourEnd={20}
              resHeight={20}
              snapRatio={1}
              days={days}
              onChange={setDays}
              calendars={calendars}
              onDoubleClick={
                handleDoubleClick // Handle double-click for single day event
              }
              onEventSelect={handleEventSelect}
              onEventEdit={handleEventEdit}
              onUpdate={handleUpdate}
              useSidebar={useSidebar}
              data={selectionData}
              onClose={handleClosePopper}
              onCloseSidebar={handleCloseSidebar}
              isDesktop={isDesktop}
              onFeedback={handleFeedback}
              onDelete={handleDelete}
              editMode={editMode}
              setEditMode={setEditMode}
              selectedPeriod={selectedPeriod}
              onCreateEventInDayView={handleCreateEventInDayView}
            />
          )}
          <Feedback state={alertState} setState={setAlertState} />

          <ExoCalPopper
            anchorEl={popperAnchor}
            open={popperOpen}
            onClose={handleClosePopper}
            data={selectionData}
            calendars={calendars}
            onFeedback={handleFeedback}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            editMode={editMode}
            setEditMode={setEditMode}
          />
        </Box>
        {/* {isDesktop && openBlockedTimes && (
          <BlockedUserTimes
            openBlockedTimes={openBlockedTimes}
            blockedUserTimesData={blockedUserTimesData}
          />
        )} */}
      </Box>
    );
  }
);

export default ExoCal;
